import { useState } from "react";
import { CloudUpload, Folder } from "lucide-react";

import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  Input,
  Label,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { logger } from "@/lib/logger";

interface OneDriveSyncDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSync: () => Promise<void>;
  onChooseFolder: (e: React.MouseEvent<HTMLButtonElement>) => void;
  fileName: string;
  path?: string;
}

export function OneDriveSyncDialog({
  isOpen,
  onClose,
  onSync,
  onChooseFolder,
  fileName,
  path,
}: OneDriveSyncDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  // The only thing the user can change here is the target folder, so the
  // discard guard compares against the folder the dialog opened on.
  const [openedWithPath, setOpenedWithPath] = useState(path);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setOpenedWithPath(path);
  }

  const handleSync = async () => {
    try {
      setIsLoading(true);
      await onSync();
      onClose();
    } catch (error) {
      logger.error("Failed to sync document to OneDrive/SharePoint", {
        operation: "syncDocumentToSharePoint",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
      isDirty={path !== openedWithPath}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CloudUpload className="size-5" />
          Synkroniser til OneDrive/SharePoint
        </DialogTitle>
        <DialogDescription>
          Upload "{fileName}" til din OneDrive/SharePoint konto.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="path">Placering</Label>
          <Input
            id="path"
            value={`/${path || "Documents"}`}
            readOnly
            className="bg-muted/50"
          />
        </div>
      </div>
      <DialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-0">
        <Button
          type="button"
          variant="outline"
          onClick={onChooseFolder}
          disabled={isLoading}
          className="mb-2 sm:mb-0"
        >
          <Folder className="mr-2 size-4" />
          Vælg en anden mappe
        </Button>
        <Button type="button" onClick={handleSync} disabled={isLoading}>
          {isLoading ? (
            <Spinner className="size-4 text-primary-foreground" />
          ) : (
            "OK"
          )}
        </Button>
      </DialogFooter>
    </FormDialog>
  );
}
